import { useCallback, useMemo } from 'react';

import {
  type PerformanceReviewPeriodInput,
  useGetCurrentEmployeePermissionAccessFlagPerformanceReviewDeleteLazyQuery,
} from '@/common/graphql';

import { useEmployeeDetailContext } from '../../contexts';

export const useEmployeeAccess = () => {
  const { currentEmployee, employeeDetail, currentEmployeeAccessFlag: accessFlag } = useEmployeeDetailContext();

  const [loadPerformanceReviewDelete] = useGetCurrentEmployeePermissionAccessFlagPerformanceReviewDeleteLazyQuery();

  const isSuperAdmin = useMemo(
    () => currentEmployee?.accessFlag?.isSuperAdmin ?? false,
    [currentEmployee?.accessFlag?.isSuperAdmin],
  );

  const isAdminStandard = useMemo(
    () => currentEmployee?.accessFlag?.isAdminStandard ?? false,
    [currentEmployee?.accessFlag?.isAdminStandard],
  );

  const isMyOwnData = useMemo(() => accessFlag?.isMyOwnData ?? false, [accessFlag?.isMyOwnData]);

  const isDirectReport = useMemo(() => accessFlag?.isDirectReport ?? false, [accessFlag?.isDirectReport]);

  const hasEmployeeGeneralInformationEditAccess = useMemo(
    () => accessFlag?.employeePersonalInformationEdit ?? false,
    [accessFlag?.employeePersonalInformationEdit],
  );

  const hasEmployeePersonalInformationViewAccess = useMemo(
    () => accessFlag?.employeePersonalInformationView ?? false,
    [accessFlag?.employeePersonalInformationView],
  );

  const hasEmployeePersonalInformationEditAccess = useMemo(
    () => accessFlag?.employeePersonalInformationEdit ?? false,
    [accessFlag?.employeePersonalInformationEdit],
  );

  const hasEmployeePersonalInformationDeleteAccess = useMemo(
    () => accessFlag?.employeePersonalInformationDelete ?? false,
    [accessFlag?.employeePersonalInformationDelete],
  );

  const hasEmployeeOffBoardingViewAccess = useMemo(
    () => accessFlag?.employeeOffBoardingView ?? false,
    [accessFlag?.employeeOffBoardingView],
  );

  const hasEmployeeOffBoardingEditAccess = useMemo(
    () => accessFlag?.employeeOffBoardingEdit ?? false,
    [accessFlag?.employeeOffBoardingEdit],
  );

  const hasEmployeeDeleteAccess = useMemo(() => accessFlag?.employeeDelete ?? false, [accessFlag?.employeeDelete]);

  const hasEmployeeJobContractHistoryViewAccess = useMemo(
    () => accessFlag?.employeeJobContractHistoryView ?? false,
    [accessFlag?.employeeJobContractHistoryView],
  );

  const hasEmployeeJobContractHistoryEditAccess = useMemo(
    () => accessFlag?.employeeJobContractHistoryEdit ?? false,
    [accessFlag?.employeeJobContractHistoryEdit],
  );

  const hasEmployeeJobContractHistoryDeleteAccess = useMemo(
    () => accessFlag?.employeeJobContractHistoryDelete ?? false,
    [accessFlag?.employeeJobContractHistoryDelete],
  );

  const hasEmployeePermissionViewAccess = useMemo(
    () => accessFlag?.employeePermissionView ?? false,
    [accessFlag?.employeePermissionView],
  );

  const hasEmployeePermissionEditAccess = useMemo(
    () => accessFlag?.employeePermissionEdit ?? false,
    [accessFlag?.employeePermissionEdit],
  );

  const hasProbationReviewViewAccess = useMemo(
    () => accessFlag?.probationReviewView ?? false,
    [accessFlag?.probationReviewView],
  );

  const hasProbationReviewModifyAccess = useMemo(
    () => accessFlag?.probationReviewModify ?? false,
    [accessFlag?.probationReviewModify],
  );

  const hasPerformanceReviewViewAccess = useMemo(
    () => accessFlag?.performanceReviewView ?? false,
    [accessFlag?.performanceReviewView],
  );

  const hasPerformanceReviewResultsListAccess = useMemo(
    () => currentEmployee?.accessFlag?.performanceReviewResultsList ?? false,
    [currentEmployee?.accessFlag?.performanceReviewResultsList],
  );

  const hasPerformanceReviewStageAccess = useMemo(
    () => currentEmployee?.accessFlag?.performanceReviewStage ?? false,
    [currentEmployee?.accessFlag?.performanceReviewStage],
  );

  const hasPerformanceReviewDeleteAccess = useCallback(
    async (periods: PerformanceReviewPeriodInput[]) => {
      const result = await loadPerformanceReviewDelete({
        variables: {
          employeeId: employeeDetail.id,
          periods,
        },
      });
      return (
        result.data?.currentEmployee?.accessFlag?.performanceReviewDelete ??
        periods.map(it => ({ period: it, isDeletable: false }))
      );
    },
    [employeeDetail.id, loadPerformanceReviewDelete],
  );

  const hasEmployeeAddAccess = useMemo(
    () => currentEmployee?.accessFlag?.employeeAdd ?? false,
    [currentEmployee?.accessFlag?.employeeAdd],
  );

  const hasEmployeeAlertListViewAccess = useMemo(
    () => currentEmployee?.accessFlag?.employeeAlertListView ?? false,
    [currentEmployee?.accessFlag?.employeeAlertListView],
  );

  const hasReportViewAccess = useMemo(
    () => currentEmployee?.accessFlag?.reportView ?? false,
    [currentEmployee?.accessFlag?.reportView],
  );

  const hasEmployeeExternalAccountViewAccess = useMemo(
    () => accessFlag?.employeeExternalAccountView ?? false,
    [accessFlag?.employeeExternalAccountView],
  );

  const hasEmployeeExternalAccountModifyAccess = useMemo(
    () => accessFlag?.employeeExternalAccountModify ?? false,
    [accessFlag?.employeeExternalAccountModify],
  );

  const hasSettingViewAccess = useMemo(
    () => currentEmployee?.accessFlag?.settingView ?? false,
    [currentEmployee?.accessFlag?.settingView],
  );

  const hasEmployeeManagerialAccountingViewAccess = useMemo(
    () => accessFlag?.employeeManagerialAccountingView ?? false,
    [accessFlag?.employeeManagerialAccountingView],
  );

  const hasEmployeeManagerialAccountingEditAccess = useMemo(
    () => accessFlag?.employeeManagerialAccountingEdit ?? false,
    [accessFlag?.employeeManagerialAccountingEdit],
  );

  return {
    hasEmployeeAddAccess,
    hasEmployeeDeleteAccess,
    hasEmployeeExternalAccountModifyAccess,
    hasEmployeeExternalAccountViewAccess,
    hasEmployeeGeneralInformationEditAccess,
    hasEmployeeAlertListViewAccess,
    hasEmployeeJobContractHistoryDeleteAccess,
    hasEmployeeJobContractHistoryEditAccess,
    hasEmployeeJobContractHistoryViewAccess,
    hasEmployeeOffBoardingEditAccess,
    hasEmployeeOffBoardingViewAccess,
    hasEmployeePermissionEditAccess,
    hasEmployeePermissionViewAccess,
    hasEmployeePersonalInformationDeleteAccess,
    hasEmployeePersonalInformationEditAccess,
    hasEmployeePersonalInformationViewAccess,
    hasPerformanceReviewDeleteAccess,
    hasPerformanceReviewResultsListAccess,
    hasPerformanceReviewStageAccess,
    hasPerformanceReviewViewAccess,
    hasProbationReviewModifyAccess,
    hasProbationReviewViewAccess,
    hasReportViewAccess,
    hasSettingViewAccess,
    hasEmployeeManagerialAccountingViewAccess,
    hasEmployeeManagerialAccountingEditAccess,
    isAdminStandard,
    isDirectReport,
    isMyOwnData,
    isSuperAdmin,
  };
};
